import React, { useState } from 'react';
import Card from '../../../Components/Ui/Card';
import Button from '../../../Components/Ui/Button';
import OrderProgress from '../../../Components/Ui/OrderProgress';
import Badge from '../../../Components/Ui/Badge';
import { ArrowLeft, Package, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUpdateOrderMutation } from '../../../app/services/orderApi';
import { toast } from 'react-toastify';

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const statusVariants = {
  pending: 'warning',
  processing: 'info',
  shipped: 'primary',
  delivered: 'success',
  cancelled: 'error',
};

const paymentVariants = {
  paid: 'success',
  unpaid: 'warning',
  failed: 'error',
};

const StatusUpdateModal = ({ isOpen, onClose, currentStatus, onUpdate, isUpdatingStatus }) => {
  const [selectedNewStatus, setSelectedNewStatus] = useState(currentStatus);

  const availableStatuses = Object.keys(statusVariants);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Update Order Status</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>
        <p className="mb-4 text-sm">
          Current Status:{' '}
          <Badge variant={statusVariants[currentStatus] || 'primary'}>
            {capitalize(currentStatus || 'pending')}
          </Badge>
        </p>
        <div className="mb-6">
          <label htmlFor="newStatus" className="block text-sm font-medium text-gray-700 mb-2">
            Select New Status
          </label>
          <select
            id="newStatus"
            value={selectedNewStatus}
            onChange={(e) => setSelectedNewStatus(e.target.value)}
            className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-primary focus:border-primary focus:outline-none focus:ring-2"
          >
            {availableStatuses.map((status) => (
              <option key={status} value={status}>
                {capitalize(status)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onUpdate(selectedNewStatus)} disabled={isUpdatingStatus}>
            {isUpdatingStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

const OrderDetailsPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [order, setOrder] = useState(state?.order);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderMutation();

  if (!order) {
    return (
      <div className="text-center text-gray-500 mt-10 text-lg">No order details available.</div>
    );
  }

  const handleUpdateStatus = async (newStatus) => {
    try {
      await updateOrder({ id: order._id, status: newStatus }).unwrap();
      toast.success('Order status updated successfully');
      setOrder({ ...order, status: newStatus });
      setIsStatusModalOpen(false);
    } catch (error) {
      toast.error(error.data?.message || 'Failed to update order status');
    }
  };

  const getStatusBadge = (status) => (
    <Badge variant={statusVariants[status] || 'primary'}>{capitalize(status || 'pending')}</Badge>
  );

  const getPaymentStatusBadge = (status) => (
    <Badge variant={paymentVariants[status] || 'primary'}>{capitalize(status || 'pending')}</Badge>
  );

  return (
    <div className="px-4 md:px-0 py-2 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg xl:text-2xl font-bold pl-5">Order Details</h2>
        <Button onClick={() => navigate('/seller/orders')}>
          <ArrowLeft size={16} />
          Back
        </Button>
      </div>
      <Card title={`Order Details - ${order.orderId}`}>
        <div className="mb-8">
          <div className="mb-2">{getStatusBadge(order.status)}</div>
          <OrderProgress status={order.status} timestamps={order.timestamps} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Retailer</h4>
            <p className="lg:text-lg font-medium">{order.user?.name || 'Unknown'}</p>
            <p className="text-sm text-gray-500">{order.user?.email || 'N/A'}</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Payment</h4>
            <div className="flex items-center space-x-2 flex-wrap">
              {getPaymentStatusBadge(order.paymentStatus)}
              <span className="text-sm">{order.paymentMethod || 'N/A'}</span>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Shipping Address</h4>
            <p className="text-sm leading-5">
              {order.shippingAddress?.street}
              <br />
              {order.shippingAddress?.city}, {order.shippingAddress?.state}{' '}
              {order.shippingAddress?.zip}
              <br />
              {order.shippingAddress?.country}
            </p>
          </div>
        </div>

        {order.specialInstructions && (
          <div className="mb-6 p-4 bg-primary-light rounded-lg">
            <h4 className="text-sm font-medium text-primary mb-1">Special Instructions</h4>
            <p className="text-sm">{order.specialInstructions}</p>
          </div>
        )}

        <div className="border-t border-gray-200 pt-6">
          <h4 className="lg:text-lg font-medium mb-4">Products</h4>
          <div className="space-y-4">
            {order.products?.map((product, index) => (
              <div
                key={product._id || index}
                className="flex flex-col sm:flex-row sm:items-center p-4 bg-gray-50 rounded-lg gap-4"
              >
                <div className="h-12 w-12 lg:h-16 lg:w-16 bg-gray-200 rounded-md flex items-center justify-center shrink-0">
                  <Package size={24} className="text-gray-500" />
                </div>
                <div className="flex-1">
                  <h5 className="font-medium">{product.productsId?.title || 'Unknown Product'}</h5>
                  <p className="text-sm text-gray-500">SKU: {product.productsId?.sku || 'N/A'}</p>
                </div>
                <div className="text-right sm:text-left">
                  <p className="font-medium">₹{(product.price || 0).toFixed(2)}</p>
                  <p className="text-sm text-gray-500">Qty: {product.quantity || 0}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 mt-6 pt-6">
          <div className="flex justify-between mb-2 text-sm flex-wrap">
            <p>Subtotal</p>
            <p>₹{(order.subtotal || 0).toFixed(2)}</p>
          </div>
          <div className="flex justify-between mb-2 text-sm flex-wrap">
            <p>Tax (GST)</p>
            <p>₹{(order.gstAmount || 0).toFixed(2)}</p>
          </div>
          <div className="flex justify-between mb-2 text-sm flex-wrap">
            <p>Shipping</p>
            <p className={order.shippingCharge === 0 ? 'text-green-600 font-medium' : ''}>
              {order.shippingCharge === 0 ? 'FREE' : `₹${(order.shippingCharge || 0).toFixed(2)}`}
            </p>
          </div>
          <div className="flex justify-between font-medium lg:text-lg pt-2 border-t flex-wrap">
            <p>Total</p>
            <p>₹{(order.totalAmount || 0).toFixed(2)}</p>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => toast.info('Process Return functionality not yet implemented.')}
          >
            Process Return
          </Button>
          <Button
            variant="outline"
            onClick={() => toast.info('Print Invoice functionality not yet implemented.')}
          >
            Print Invoice
          </Button>
          <Button className="text-sm" onClick={() => setIsStatusModalOpen(true)}>
            Update Status
          </Button>
        </div>
      </Card>

      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        currentStatus={order.status}
        onUpdate={handleUpdateStatus}
        isUpdatingStatus={isUpdating}
      />
    </div>
  );
};

export default OrderDetailsPage;
